import { useContext, useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import StockModule from 'highcharts/modules/stock';
import { formatterGbp } from '../../utils';
import { AppContext } from '../../context/ContextProvider';
import { useTheme } from '../../context/ThemeContext';

// Import the stock module for navigator

// Initialize the stock module
if (typeof StockModule === 'function') {
  StockModule(Highcharts);
}

function formatChartDate(d: Date, locale = 'en-GB'): string {
  const month = Intl.DateTimeFormat(locale, { month: 'short' }).format(d);
  const year = Intl.DateTimeFormat(locale, { year: 'numeric' }).format(d);
  return `${month} ${year}`;
}

interface PerformanceStoryDataPoint {
  x: number;
  y: number;
  netCapital?: number;
  netPosition?: number;
  returnPct?: number;
  drawdown?: number;
}

export interface PerformanceStorySeries {
  id: string;
  name: string;
  color?: string;
  type?: 'line' | 'column'; // Support line and column (bar) chart types
  data: PerformanceStoryDataPoint[];
}

type PeriodKey = '1Y' | '3Y' | '5Y' | 'All';

interface PerformanceStoryChartProps {
  series: PerformanceStorySeries[];
  drawdownPct?: number;
  selectedPeriod: PeriodKey;
  onPeriodChange: (period: PeriodKey) => void;
}

const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: '1Y', label: '1Y' },
  { key: '3Y', label: '3Y' },
  { key: '5Y', label: '5Y' },
  { key: 'All', label: 'All' },
];

const lightThemeColors = {
  background: '#FFFFFF',
  plotBackground: '#FCFCFC',
  gridLine: '#E6E6E6',
  text: '#000000',
  textSecondary: '#666666',
  seriesLine: '#8B7CE8',
  tooltip: '#FFFFFF',
  tooltipBorder: '#E6E6E6',
  navigatorMask: 'rgba(0,0,0,0.05)',
  navigatorOutline: '#CCCCCC',
  periodButton: '#F2F2F2',
  periodButtonActive: '#E6E6E6',
  drawdownBadge: 'rgba(220, 38, 38, 0.1)',
  drawdownText: '#DC2626',
  positive: '#059669',
  negative: '#DC2626',
};

const darkThemeColors = {
  background: '#0A1628',
  plotBackground: '#0F1E32',
  gridLine: '#1E3A50',
  text: '#FFFFFF',
  textSecondary: '#8B9CB3',
  seriesLine: '#8B7CE8',
  tooltip: '#1A3A52',
  tooltipBorder: '#2A5A72',
  navigatorMask: 'rgba(255,255,255,0.1)',
  navigatorOutline: '#3A5A72',
  periodButton: '#1A2E44',
  periodButtonActive: '#3A5070',
  drawdownBadge: 'rgba(220, 38, 38, 0.2)',
  drawdownText: '#EF4444',
  positive: '#10B981',
  negative: '#EF4444',
};

export function PerformanceStoryChart({
  series,
  selectedPeriod,
  onPeriodChange,
}: PerformanceStoryChartProps): JSX.Element {
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const {
    state: {
      settings: { language },
    },
  } = useContext(AppContext);
  const { isDark } = useTheme();

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  // Select theme colors based on current theme
  const themeColors = isDark ? darkThemeColors : lightThemeColors;

  useEffect(() => {
    if (!series || series.length === 0) return;

    const options: Highcharts.Options = {
      chart: {
        type: 'line',
        backgroundColor: themeColors.background,
        plotBackgroundColor: themeColors.plotBackground,
        height: 420,
        marginTop: 10,
        spacingTop: 10,
        spacingBottom: 10,
        style: {
          fontFamily: 'GT Flexa, sans-serif',
        },
      },
      accessibility: {
        enabled: false,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        lineColor: themeColors.gridLine,
        tickColor: themeColors.gridLine,
        crosshair: {
          width: 1,
          color: themeColors.textSecondary,
          dashStyle: 'Dash',
        },
        labels: {
          style: {
            color: themeColors.textSecondary,
            fontSize: '11px',
          },
          formatter: function () {
            const date = new Date(this.value as number);
            const month = Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date);
            const year = date.getFullYear().toString().slice(-2);
            return `${month} ${year}`;
          },
        },
        gridLineWidth: 0,
        tickLength: 0,
      },
      yAxis: {
        title: {
          text: '',
        },
        crosshair: {
          width: 1,
          color: themeColors.textSecondary,
          dashStyle: 'Dash',
        },
        gridLineColor: themeColors.gridLine,
        gridLineDashStyle: 'Dash',
        labels: {
          style: {
            color: themeColors.textSecondary,
            fontSize: '11px',
          },
          formatter: function () {
            const value = this.value as number;
            if (value >= 1000000) {
              return `£${(value / 1000000).toFixed(1)}M`;
            }
            if (value >= 1000) {
              return `£${(value / 1000).toFixed(0)}K`;
            }
            return `£${value}`;
          },
        },
        lineWidth: 0,
      },
      tooltip: {
        useHTML: true,
        shared: true,
        split: false,
        followPointer: true,
        backgroundColor: themeColors.tooltip,
        borderColor: themeColors.tooltipBorder,
        borderRadius: 12,
        borderWidth: 1,
        shadow: {
          offsetX: 0,
          offsetY: 4,
          opacity: 0.15,
          width: 8,
          color: '#000000',
        },
        padding: 0,
        style: {
          color: themeColors.text,
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const xValue = this.x;

          // Skip tooltip for navigator series or invalid data
          if (xValue === undefined) {
            return false;
          }

          // Find the NAV series point which has complete tooltip data
          // The NAV series contains netCapital, netPosition, returnPct, and drawdown
          let pointOptions: PerformanceStoryDataPoint | undefined;
          let navValue: number | undefined;

          // Look through all points to find one with complete tooltip data
          if (this.points) {
            for (const p of this.points) {
              const opts = p.point?.options as PerformanceStoryDataPoint | undefined;
              if (opts?.netCapital !== undefined) {
                pointOptions = opts;
                navValue = p.y ?? undefined;
                break;
              }
            }
          }

          // Fallback to first point if no NAV series found
          if (!pointOptions && this.points?.[0]) {
            pointOptions = this.points[0].point?.options as PerformanceStoryDataPoint | undefined;
            navValue = this.points[0].y ?? undefined;
          }

          if (!pointOptions) {
            return false;
          }

          const date = formatChartDate(new Date(xValue as number), language);
          const nav = navValue !== undefined ? formatterGbp.format(navValue) : '-';
          const netCapital = pointOptions?.netCapital ? formatterGbp.format(pointOptions.netCapital) : '-';
          const netPosition = pointOptions?.netPosition
            ? `${pointOptions.netPosition >= 0 ? '+' : ''}${formatterGbp.format(pointOptions.netPosition)}`
            : '-';
          const returnPct =
            pointOptions?.returnPct !== undefined
              ? `${pointOptions.returnPct >= 0 ? '+' : ''}${pointOptions.returnPct.toFixed(1)}%`
              : '-';

          const returnColor =
            pointOptions?.returnPct !== undefined && pointOptions.returnPct >= 0
              ? themeColors.positive
              : themeColors.negative;
          const positionColor =
            pointOptions?.netPosition !== undefined && pointOptions.netPosition >= 0
              ? themeColors.positive
              : themeColors.negative;

          return `
            <div style="padding: 16px 20px; min-width: 200px; font-family: 'GT Flexa', sans-serif;">
              <div style="font-size: 16px; font-weight: 600; margin-bottom: 16px; color: ${themeColors.text};">${date}</div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <span style="color: ${themeColors.textSecondary}; font-size: 14px;">NAV</span>
                <span style="color: ${themeColors.text}; font-weight: 600; font-size: 14px;">${nav}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <span style="color: ${themeColors.textSecondary}; font-size: 14px;">Net Capital</span>
                <span style="color: ${themeColors.text}; font-weight: 600; font-size: 14px;">${netCapital}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <span style="color: ${themeColors.textSecondary}; font-size: 14px;">Net Position</span>
                <span style="color: ${positionColor}; font-weight: 600; font-size: 14px;">${netPosition}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <span style="color: ${themeColors.textSecondary}; font-size: 14px;">Return</span>
                <span style="color: ${returnColor}; font-weight: 600; font-size: 14px;">${returnPct}</span>
              </div>
            </div>
          `;
        },
      },
      navigator: {
        enabled: true,
        height: 40,
        margin: 20,
        maskFill: themeColors.navigatorMask,
        outlineColor: themeColors.navigatorOutline,
        outlineWidth: 1,
        handles: {
          backgroundColor: themeColors.periodButtonActive,
          borderColor: themeColors.navigatorOutline,
        },
        series: {
          color: themeColors.seriesLine,
          lineWidth: 1,
        },
        xAxis: {
          labels: {
            style: {
              color: themeColors.textSecondary,
              fontSize: '10px',
            },
          },
        },
      },
      scrollbar: {
        enabled: false,
      },
      rangeSelector: {
        enabled: false,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                radius: 6,
                lineWidth: 3,
                lineColor: themeColors.background,
                fillColor: themeColors.seriesLine,
              },
            },
          },
          states: {
            hover: {
              lineWidthPlus: 0,
              halo: {
                size: 12,
                opacity: 0.25,
              },
            },
          },
        },
        line: {
          lineWidth: 2.5,
        },
        column: {
          borderWidth: 0,
          borderRadius: 2,
          groupPadding: 0.1,
          pointPadding: 0.05,
        },
      },
      series: series.map((s) => ({
        type: s.type || 'line',
        name: s.name,
        color: s.color || themeColors.seriesLine,
        data: s.data.map((point) => ({
          x: point.x,
          y: point.y,
          netCapital: point.netCapital,
          netPosition: point.netPosition,
          returnPct: point.returnPct,
          drawdown: point.drawdown,
        })),
        // For column charts, show them behind line charts
        ...(s.type === 'column' ? { zIndex: 0 } : { zIndex: 1 }),
      })) as Highcharts.SeriesOptionsType[],
    };

    setChartOptions(options);
  }, [series, language, themeColors, isDark]);

  // Reset navigator extremes when period changes so the chart shows the full data range
  useEffect(() => {
    const chart = chartRef.current;
    if (chart && chart.xAxis?.[0]) {
      chart.xAxis[0].setExtremes(undefined, undefined, true, false);
    }
  }, [selectedPeriod, series]);

  const afterChartCreated = (chart: Highcharts.Chart) => {
    chartRef.current = chart || null;
  };

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: themeColors.background }}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 px-5 pt-5 pb-3">
        <h3 className="text-lg font-medium" style={{ color: themeColors.text }}>
          Performance Story
        </h3>
        <div className="flex items-center gap-3">
          {/* Period Shortcuts */}
          <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: themeColors.periodButton }}>
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.key}
                onClick={() => onPeriodChange(option.key)}
                className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                style={{
                  backgroundColor: selectedPeriod === option.key ? themeColors.periodButtonActive : 'transparent',
                  color: selectedPeriod === option.key ? themeColors.text : themeColors.textSecondary,
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        callback={afterChartCreated}
      />

      {/* Legend */}
      <div className="flex items-center gap-6 px-5 pb-4">
        {series.map((s) => (
          <div key={s.id} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || themeColors.seriesLine }} />
            <span className="text-sm" style={{ color: themeColors.textSecondary }}>
              {s.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
